import { useState } from 'react';
import { UserRole, ROLE_LEVELS } from '@wineryxplorer/shared';
import { trpc } from '../../lib/trpc';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { showToast } from '../../utils/toast';

interface AdminCreateUserModalProps {
  onClose: () => void;
  /** Called after a successful create — parent should refetch + close. */
  onCreated: () => void;
}

const ROLE_LABELS: Record<string, string> = {
  [UserRole.USER]: 'User',
  [UserRole.VENUE_ADMIN]: 'Venue Admin',
  [UserRole.SITE_ADMIN]: 'Site Admin',
};

// Roles an admin can assign at creation, lowest first. SUPER_ADMIN is never
// assignable here; the server enforces the same ceiling.
const ASSIGNABLE_ROLES = [
  UserRole.USER,
  UserRole.VENUE_ADMIN,
  UserRole.SITE_ADMIN,
] as const;

const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export const AdminCreateUserModal = ({
  onClose,
  onCreated,
}: AdminCreateUserModalProps) => {
  const { user: currentUser } = useAuth();
  const [email, setEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRole>(UserRole.USER);
  const [formError, setFormError] = useState('');

  // Mirror the server clamp: SUPER_ADMIN assigns up to SITE_ADMIN,
  // SITE_ADMIN up to VENUE_ADMIN.
  const maxLevel =
    currentUser?.role === UserRole.SUPER_ADMIN
      ? ROLE_LEVELS[UserRole.SITE_ADMIN]
      : currentUser?.role === UserRole.SITE_ADMIN
        ? ROLE_LEVELS[UserRole.VENUE_ADMIN]
        : 0;
  const assignableRoles = ASSIGNABLE_ROLES.filter(
    (r) => ROLE_LEVELS[r] <= maxLevel
  );

  const createUser = trpc.adminUsers.create.useMutation({
    onSuccess: (result) => {
      if (result.emailSent) {
        showToast(
          `User created — a setup email was sent to ${result.user.email}.`,
          'success'
        );
      } else {
        showToast(
          'User created, but the setup email failed to send. They can use "Forgot password" to set their password.',
          'error'
        );
      }
      onCreated();
    },
    onError: (err) => setFormError(err.message),
  });

  const canSubmit =
    isValidEmail(email) &&
    userName.trim().length > 0 &&
    name.trim().length > 0 &&
    !createUser.isPending;

  const handleSubmit = () => {
    setFormError('');
    if (!canSubmit) {
      setFormError('Enter a valid email, a username, and a name.');
      return;
    }
    createUser.mutate({
      email: email.trim(),
      userName: userName.trim(),
      name: name.trim(),
      role,
    });
  };

  const handleBackdrop = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !createUser.isPending) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 p-4 pt-16"
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Create user"
    >
      <div className="w-full max-w-md rounded-lg bg-gray-50 shadow-xl">
        {/* Header + action buttons (top-right, per repo form convention) */}
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <h2 className="text-lg font-bold text-gray-900">Create user</h2>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="primary"
              onClick={handleSubmit}
              loading={createUser.isPending}
              disabled={!canSubmit}
            >
              Create
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onClose}
              disabled={createUser.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>

        <div className="space-y-4 p-4">
          {formError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {formError}
            </div>
          )}

          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setFormError('');
            }}
            placeholder="person@example.com"
            fullWidth
            required
            disabled={createUser.isPending}
          />

          <Input
            label="Username"
            type="text"
            value={userName}
            onChange={(e) => {
              setUserName(e.target.value);
              setFormError('');
            }}
            placeholder="janedoe"
            fullWidth
            required
            disabled={createUser.isPending}
          />

          <Input
            label="Name"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setFormError('');
            }}
            placeholder="Jane Doe"
            fullWidth
            required
            disabled={createUser.isPending}
          />

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              disabled={createUser.isPending}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-info-500 focus:ring-1 focus:ring-info-500"
            >
              {assignableRoles.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r] ?? r}
                </option>
              ))}
            </select>
          </div>

          <p className="text-xs text-gray-500">
            The new user gets an email with a link to set their password. No
            password is set here.
          </p>
        </div>
      </div>
    </div>
  );
};
